import React from "react";
import { AlertTriangle, Minus, Plus, QrCode, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { itemColor } from "./SheetMap";
import {
  MAX_NAME_LENGTH,
  MAX_NOTE_LENGTH,
  MAX_QUANTITY,
  clampQuantity,
  hasDateConflict,
  isPrintable,
} from "./labelSheet";

const inputClass =
  "w-full rounded-lg border border-border bg-card px-2.5 text-base text-foreground transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 md:text-[13px]";

// Switch compacto do mockup (o padrão do shadcn é maior)
const smallSwitch =
  "h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3";

const fieldLabelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";

/** Fabricação/Validade: data (switch ligado) ou texto livre (desligado) */
function DateField({ id, title, useDate, date, text, textPlaceholder, invalid, onToggle, onDate, onText }) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-surface-low p-2",
        invalid ? "border-destructive/60" : "border-border"
      )}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-xs font-medium text-foreground">
          {title}
        </label>
        <Switch
          checked={useDate}
          onCheckedChange={onToggle}
          aria-label={`${title}: usar data`}
          title={useDate ? "Usando data — desligue para texto livre" : "Usando texto livre — ligue para data"}
          className={smallSwitch}
        />
      </div>
      {useDate ? (
        <input
          id={id}
          type="date"
          value={date}
          onChange={(e) => onDate(e.target.value)}
          aria-invalid={invalid || undefined}
          className={cn(inputClass, "h-9 px-2")}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={text}
          maxLength={24}
          onChange={(e) => onText(e.target.value)}
          placeholder={textPlaceholder}
          className={cn(inputClass, "h-9 px-2")}
        />
      )}
    </div>
  );
}

/** Um produto da lista: nome, tiragem, datas, QR Code e texto de rodapé */
function LabelItemCard({ entry, index, datalistId, onChange, onRemove }) {
  const set = (field) => (value) => onChange(entry.id, { [field]: value });
  const printable = isPrintable(entry);
  const conflict = hasDateConflict(entry);
  const title = entry.name.trim() || `Produto ${index + 1}`;
  const fieldId = (name) => `label-${entry.id}-${name}`;

  const pending = !entry.name.trim()
    ? "Informe o nome para imprimir"
    : entry.quantity === 0
    ? "Quantidade zerada — não será impresso"
    : null;

  return (
    <article
      className={cn(
        "rounded-2xl border bg-card p-3.5 shadow-soft transition-colors",
        printable ? "border-border" : "border-dashed border-border"
      )}
      aria-label={title}
    >
      {/* Topo: número, nome, tiragem e remoção */}
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/70 pb-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
              printable ? itemColor(index) : "bg-muted text-muted-foreground"
            )}
          >
            {index + 1}
          </span>
          <h3
            className={cn(
              "truncate font-display text-sm font-bold md:text-base",
              entry.name.trim() ? "text-primary" : "text-muted-foreground"
            )}
          >
            {title}
          </h3>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          <span className="rounded-md border border-border bg-surface px-2 py-0.5 text-[11px] font-semibold text-primary">
            {entry.quantity} un
          </span>
          <button
            type="button"
            onClick={() => onRemove(entry.id)}
            aria-label={`Remover ${title}`}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {/* Produto e quantidade */}
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-7 sm:col-span-8">
            <label htmlFor={fieldId("name")} className={fieldLabelClass}>
              Produto
            </label>
            <input
              id={fieldId("name")}
              type="text"
              list={datalistId}
              value={entry.name}
              maxLength={MAX_NAME_LENGTH}
              onChange={(e) => set("name")(e.target.value)}
              placeholder="Digite ou escolha o produto"
              autoComplete="off"
              className={cn(inputClass, "h-10 font-medium")}
            />
          </div>
          <div className="col-span-5 sm:col-span-4">
            <label htmlFor={fieldId("qty")} className={fieldLabelClass}>
              Qtd. etiquetas
            </label>
            <div className="flex h-10 items-center overflow-hidden rounded-lg border border-border bg-surface-low focus-within:border-primary focus-within:ring-[3px] focus-within:ring-brand-pistachio/25">
              <button
                type="button"
                onClick={() => set("quantity")(clampQuantity(entry.quantity - 1))}
                disabled={entry.quantity <= 0}
                aria-label="Diminuir quantidade"
                className="flex h-full w-9 flex-shrink-0 items-center justify-center text-primary transition-colors hover:bg-secondary active:bg-brand-pistachio/40 disabled:opacity-40"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <input
                id={fieldId("qty")}
                type="text"
                inputMode="numeric"
                value={entry.quantity}
                onChange={(e) => set("quantity")(clampQuantity(e.target.value))}
                onFocus={(e) => e.target.select()}
                aria-describedby={fieldId("qty-hint")}
                className="h-full w-full min-w-0 border-0 bg-transparent p-0 text-center text-base font-bold text-primary focus:outline-none md:text-sm"
              />
              <button
                type="button"
                onClick={() => set("quantity")(clampQuantity(entry.quantity + 1))}
                disabled={entry.quantity >= MAX_QUANTITY}
                aria-label="Aumentar quantidade"
                className="flex h-full w-9 flex-shrink-0 items-center justify-center text-primary transition-colors hover:bg-secondary active:bg-brand-pistachio/40 disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <span id={fieldId("qty-hint")} className="sr-only">
              Máximo de {MAX_QUANTITY} etiquetas por produto
            </span>
          </div>
        </div>

        {/* Datas */}
        <div className="grid grid-cols-2 gap-2.5">
          <DateField
            id={fieldId("fab")}
            title="Fabricação"
            useDate={entry.useFabricationDate}
            date={entry.fabrication}
            text={entry.fabricationText}
            textPlaceholder="Ex.: Lote 2026"
            invalid={conflict}
            onToggle={set("useFabricationDate")}
            onDate={set("fabrication")}
            onText={set("fabricationText")}
          />
          <DateField
            id={fieldId("val")}
            title="Validade"
            useDate={entry.useValidityDate}
            date={entry.validity}
            text={entry.validityText}
            textPlaceholder="Ex.: 30 dias"
            invalid={conflict}
            onToggle={set("useValidityDate")}
            onDate={set("validity")}
            onText={set("validityText")}
          />
        </div>
        {conflict && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-destructive" role="alert">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            A validade está antes da fabricação.
          </p>
        )}

        {/* QR Code e texto complementar */}
        <div className="space-y-2 rounded-lg border border-brand-pistachio/50 bg-secondary/40 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={fieldId("qr")} className="flex cursor-pointer items-center gap-2">
              <Switch
                id={fieldId("qr")}
                checked={entry.hasQrCode}
                onCheckedChange={set("hasQrCode")}
                className={smallSwitch}
              />
              <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <QrCode
                  className={cn("h-3.5 w-3.5", entry.hasQrCode ? "text-primary" : "text-muted-foreground")}
                />
                Incluir QR Code
              </span>
            </label>
            <span
              className={cn(
                "rounded border px-1.5 py-0.5 text-[10px]",
                entry.hasQrCode
                  ? "border-border bg-card font-semibold text-primary"
                  : "border-transparent text-muted-foreground"
              )}
            >
              {entry.hasQrCode ? "Ativo" : "Desativado"}
            </span>
          </div>
          {entry.hasQrCode && (
            <div>
              <label htmlFor={fieldId("qr-text")} className="sr-only">
                Conteúdo do QR Code
              </label>
              <input
                id={fieldId("qr-text")}
                type="text"
                value={entry.qrCodeText}
                onChange={(e) => set("qrCodeText")(e.target.value)}
                placeholder="Link ou texto do QR (ex.: https://wa.me/55...)"
                className={cn(inputClass, "h-9")}
              />
              <span className="mt-1 block text-[10px] text-muted-foreground">
                Em branco, o QR leva nome, datas e o texto complementar.
              </span>
            </div>
          )}
          <div>
            <label htmlFor={fieldId("note")} className="sr-only">
              Texto complementar
            </label>
            <input
              id={fieldId("note")}
              type="text"
              value={entry.note}
              maxLength={MAX_NOTE_LENGTH}
              onChange={(e) => set("note")(e.target.value)}
              placeholder="Ex.: Lote 001 • Manter congelado (opcional)"
              className={cn(inputClass, "h-9")}
            />
            <span className="mt-1 block text-[10px] text-muted-foreground">
              Texto complementar impresso no rodapé da etiqueta.
            </span>
          </div>
        </div>

        {pending && <p className="text-xs font-medium text-terra">{pending}</p>}
      </div>
    </article>
  );
}

export default React.memo(LabelItemCard);
